import { useEffect, useRef, useState } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'

import { authClient } from '#features/auth/auth-service.ts'
import {
  buildDesktopOAuthContinuationUrl,
  desktopAuthorizationSearchSchema,
} from '#lib/desktop-auth.ts'

export const Route = createFileRoute('/desktop/authorize')({
  validateSearch: desktopAuthorizationSearchSchema,
  beforeLoad: async ({ search, location }) => {
    if (search.provider && !search.oauth_complete) return
    const session = await authClient.getSession()
    if (!session.data?.session || !session.data.user) {
      throw redirect({ to: '/login', search: { redirectTo: location.href } })
    }
  },
  component: DesktopAuthorizePage,
})

function DesktopAuthorizePage() {
  const search = Route.useSearch()
  const [error, setError] = useState<string | null>(null)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const pendingRequest = useRef<{
    key: string
    promise: Promise<string>
  } | null>(null)
  const oauthRequest = useRef<{
    key: string
    promise: Promise<void>
  } | null>(null)

  const requestKey = [
    search.client_id,
    search.redirect_uri,
    search.state,
    search.code_challenge,
    search.oauth_complete ?? '',
    search.provider,
  ].join('\u0000')

  useEffect(() => {
    if (search.provider && !search.oauth_complete) {
      const callbackURL = buildDesktopOAuthContinuationUrl({
        origin: window.location.origin,
        provider: search.provider,
        clientId: search.client_id,
        redirectUri: search.redirect_uri,
        state: search.state,
        codeChallenge: search.code_challenge,
      })
      if (oauthRequest.current?.key !== requestKey) {
        oauthRequest.current = {
          key: requestKey,
          promise: authClient.signIn
            .oauth2({
              providerId: search.provider,
              callbackURL,
            })
            .then((result) => {
              if (result.error) throw new Error('provider_sign_in_failed')
            }),
        }
      }
      let cancelled = false
      void oauthRequest.current.promise.catch(() => {
        if (cancelled) return
        setError(
          'We could not start provider sign-in. Please try again from the Trevize desktop app.',
        )
      })
      return () => {
        cancelled = true
      }
    }
    if (pendingRequest.current?.key !== requestKey) {
      setError(null)
      setRedirectUrl(null)
      pendingRequest.current = {
        key: requestKey,
        promise: completeAuthorization({
          clientId: search.client_id,
          redirectUri: search.redirect_uri,
          state: search.state,
          codeChallenge: search.code_challenge,
        }),
      }
    }
    const request = pendingRequest.current
    if (!request) return
    let cancelled = false
    void request.promise
      .then((url) => {
        if (cancelled) return
        setRedirectUrl(url)
        // Keep a visible link below for browsers that block automatic
        // custom-protocol navigation without a user gesture.
        window.location.assign(url)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(
          cause instanceof Error && cause.message === 'invalid_request'
            ? 'This sign-in request is no longer valid. Start sign-in again from the Trevize desktop app.'
            : 'We could not authorize the Trevize desktop app. Please try again from the app.',
        )
      })
    return () => {
      cancelled = true
    }
  }, [
    requestKey,
    search.client_id,
    search.redirect_uri,
    search.state,
    search.code_challenge,
    search.oauth_complete,
    search.provider,
  ])

  return (
    <main>
      <h1>
        {error
          ? 'Desktop sign-in failed'
          : search.oauth_complete || !search.provider
            ? 'Completing Trevize sign-in…'
            : 'Continue with your provider…'}
      </h1>
      {error ? (
        <p>{error}</p>
      ) : (
        <p>You can return to the Trevize app when this is complete.</p>
      )}
      {redirectUrl && (
        <p>
          <a href={redirectUrl}>Open Trevize desktop app</a>
        </p>
      )}
    </main>
  )
}

async function completeAuthorization(input: {
  clientId: string
  redirectUri: string
  state: string
  codeChallenge: string
}): Promise<string> {
  const request = await fetch('/api/auth/desktop/request', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!request.ok) throw new Error('invalid_request')
  const requestValue: unknown = await request.json()
  if (!isRequestResponse(requestValue)) throw new Error('invalid_request')
  const response = await fetch('/api/auth/desktop/complete', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ requestId: requestValue.requestId }),
  })
  const value: unknown = await response.json()
  if (!response.ok || !isCompleteResponse(value))
    throw new Error('access_denied')
  return value.redirectUrl
}

function isRequestResponse(value: unknown): value is { requestId: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'requestId' in value &&
    typeof value.requestId === 'string' &&
    value.requestId.length > 0
  )
}

function isCompleteResponse(value: unknown): value is { redirectUrl: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'redirectUrl' in value &&
    typeof value.redirectUrl === 'string' &&
    value.redirectUrl.startsWith('trevize://auth/callback?')
  )
}
